// Calendar page load states (#14562): aria-busy while the FullCalendar events
// source fetches, a said (never only logged) failure with a Retry beside it,
// and the delegated Retry itself. Lives here rather than in the view's inline
// `:javascript` block so it is unit-tested as a module; the view wires it in
// through `window.GoatCalendarLoadState` (set in application.js, which also
// exposes the on-demand FullCalendar loader there, lib/fullcalendar_loader.js).

import { setButtonLoading } from "./button_loading"

export const CALENDAR_LOAD_FAILURE_TEXT = "Could not load your appointments. Check your connection and try again."
export const CALENDAR_RETRY_LABEL = "Retry"
// The disabled-control reason while a retry is in flight (BUTTON-4), the same
// wording the React RetryButton uses.
export const CALENDAR_RETRYING_REASON = "Retrying…"

// Returns the FullCalendar callbacks for one calendar instance:
// `loading` (the calendar option) and `failure` / `success` (the events source).
export function bindCalendarLoadState({ calendarEl, failureEl }) {
  const messageEl = failureEl ? failureEl.querySelector("[data-cal-failure-message]") : null
  const retryEl = failureEl ? failureEl.querySelector("[data-cal-retry]") : null

  // Reveal first, THEN write the message, so the live region announces it;
  // clear and hide on the next successful fetch.
  function setFailed(failed) {
    if (!failureEl) return
    if (failed) {
      failureEl.hidden = false
      if (messageEl) messageEl.textContent = CALENDAR_LOAD_FAILURE_TEXT
    } else {
      if (messageEl) messageEl.textContent = ""
      failureEl.hidden = true
    }
  }

  return {
    // Grid busy while any events fetch runs; Retry held in the shared busy
    // button state for the same span (BUTTON-8: spinner in the icon slot, label
    // kept, disabled + aria-busy) and says why it is disabled (BUTTON-4), so a
    // second click cannot stack a second fetch.
    loading(isLoading) {
      if (calendarEl) calendarEl.setAttribute("aria-busy", isLoading ? "true" : "false")
      if (!retryEl) return
      setButtonLoading(retryEl, isLoading)
      if (isLoading) {
        retryEl.setAttribute("data-gl-tooltip", CALENDAR_RETRYING_REASON)
        retryEl.setAttribute("aria-label", `${CALENDAR_RETRY_LABEL} — ${CALENDAR_RETRYING_REASON}`)
      } else {
        retryEl.removeAttribute("data-gl-tooltip")
        retryEl.removeAttribute("aria-label")
      }
    },
    failure() {
      setFailed(true)
    },
    // FullCalendar requires `success` to return the events it was given.
    success(events) {
      setFailed(false)
      return events
    },
  }
}

// Delegated Retry: refetches the same source when `target` is (inside) an
// enabled Retry button. Before any calendar exists — the FullCalendar library
// itself failed to load (#19204) — it calls `reload` instead, which loads the
// library again. Returns true when the click was a Retry.
export function retryCalendarLoad(target, calendar, reload) {
  const retry = target && typeof target.closest === "function" ? target.closest("[data-cal-retry]") : null
  if (!retry) return false
  if (retry.disabled) return true
  if (calendar) calendar.refetchEvents()
  else if (typeof reload === "function") reload()
  return true
}
